// Admin API utility functions

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('admin_token');
};

// Helper to set auth token
export const setAuthToken = (token) => {
  localStorage.setItem('admin_token', token);
};

// Helper to remove auth token
export const removeAuthToken = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
};

// Helper to get user data
export const getAdminUser = () => {
  const userJson = localStorage.getItem('admin_user');
  return userJson ? JSON.parse(userJson) : null;
};

// Helper to set user data
export const setAdminUser = (user) => {
  localStorage.setItem('admin_user', JSON.stringify(user));
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getAuthToken();
};

// API request wrapper
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || data.message || 'API request failed');
  }

  return data;
};

// Admin Auth APIs
export const adminLogin = async (username, password) => {
  const data = await apiRequest('login.php', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });

  if (data.success && data.token) {
    setAuthToken(data.token);
    setAdminUser(data.user);
  }

  return data;
};

export const adminLogout = () => {
  removeAuthToken();
};

// Messages APIs
export const getMessages = async (status = 'all', limit = 50, offset = 0) => {
  return apiRequest(`messages.php?status=${status}&limit=${limit}&offset=${offset}`);
};

export const getMessage = async (id) => {
  return apiRequest(`messages.php?id=${id}`);
};

export const updateMessageStatus = async (id, status) => {
  return apiRequest('messages.php', {
    method: 'PUT',
    body: JSON.stringify({ id, status }),
  });
};

export const deleteMessage = async (id) => {
  return apiRequest('messages.php', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
};

// Save contact form submission
export const saveContactMessage = async (messageData) => {
  return apiRequest('save-message.php', {
    method: 'POST',
    body: JSON.stringify(messageData),
  });
};

// Email Accounts APIs
export const getEmailAccounts = async () => {
  return apiRequest('get-email-accounts.php');
};

// Email APIs
export const fetchEmails = async (limit = 50, offset = 0, account_id = null) => {
  const params = new URLSearchParams({ limit, offset });
  if (account_id) params.append('account_id', account_id);
  return apiRequest(`fetch-emails.php?${params.toString()}`);
};

// Settings APIs
export const getSettings = async () => {
  return apiRequest('settings.php');
};

export const updateSettings = async (settings) => {
  return apiRequest('settings.php', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
};

export const changePassword = async (currentPassword, newPassword, confirmPassword) => {
  return apiRequest('settings.php', {
    method: 'POST',
    body: JSON.stringify({
      change_password: {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      },
    }),
  });
};

// Email Settings APIs
export const getEmailSettings = async () => {
  return apiRequest('get-email-settings.php');
};

export const updateEmailSettings = async (imap_host, imap_port, imap_username, imap_password) => {
  return apiRequest('update-email-settings.php', {
    method: 'POST',
    body: JSON.stringify({
      imap_host,
      imap_port,
      imap_username,
      imap_password
    }),
  });
};

export const testEmailConnection = async (imap_host, imap_port, imap_username, imap_password) => {
  return apiRequest('test-email-connection.php', {
    method: 'POST',
    body: JSON.stringify({
      imap_host,
      imap_port,
      imap_username,
      imap_password
    }),
  });
};

// Archived Emails APIs
export const getArchivedEmails = async (account_id = null) => {
  const params = account_id ? `?account_id=${account_id}` : '';
  return apiRequest(`get-archived-emails.php${params}`);
};

export const archiveEmail = async (uid, from, subject, account_id = null) => {
  return apiRequest('archive-email.php', {
    method: 'POST',
    body: JSON.stringify({
      uid,
      from,
      subject,
      action: 'archive',
      account_id
    }),
  });
};

export const unarchiveEmail = async (uid, account_id = null) => {
  return apiRequest('archive-email.php', {
    method: 'POST',
    body: JSON.stringify({
      uid,
      action: 'unarchive',
      account_id
    }),
  });
};

// Deleted Emails APIs
export const getDeletedEmails = async (account_id = null) => {
  const params = account_id ? `?account_id=${account_id}` : '';
  return apiRequest(`get-deleted-emails.php${params}`);
};

export const deleteEmail = async (uid, from, subject, account_id = null) => {
  return apiRequest('delete-email.php', {
    method: 'POST',
    body: JSON.stringify({
      uid,
      from,
      subject,
      action: 'delete',
      account_id
    }),
  });
};

export const restoreEmail = async (uid, account_id = null) => {
  return apiRequest('delete-email.php', {
    method: 'POST',
    body: JSON.stringify({
      uid,
      action: 'restore',
      account_id
    }),
  });
};

// Send Email API
export const sendEmail = async (to, subject, message) => {
  return apiRequest('send-email.php', {
    method: 'POST',
    body: JSON.stringify({
      to,
      subject,
      message
    }),
  });
};

// Mark Email as Read/Unread
export const markEmailAsRead = async (uid, read = true) => {
  return apiRequest('mark-email-read.php', {
    method: 'POST',
    body: JSON.stringify({
      uid,
      read
    }),
  });
};

// Permanently Delete Emails from Server (Batch)
export const permanentDeleteEmails = async (uids, account_id = null) => {
  return apiRequest('permanent-delete-emails.php', {
    method: 'POST',
    body: JSON.stringify({
      uids,
      account_id
    }),
  });
};
